import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  DollarSign,
  Calculator,
  Users,
  AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import axios from "axios";

interface EmployeeData {
  _id: string;
  fullName: string;
  email: string;
  designation?: string;
  department?: string;
  isActive: boolean;
  resignationDate?: string;
  resignationReason?: string;
}

interface SalaryFormData {
  basic: string;
  hra: string;
  otherAllowances: string;
  pf: string;
  esi: string;
}

export default function AddEditSalary() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = window.location.pathname.includes("/edit/");

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingEmployee, setIsFetchingEmployee] = useState(false);
  const [isFetchingSalary, setIsFetchingSalary] = useState(false);
  const [error, setError] = useState("");
  const [employee, setEmployee] = useState<EmployeeData | null>(null);

  const [formData, setFormData] = useState<SalaryFormData>({
    basic: "",
    hra: "",
    otherAllowances: "",
    pf: "",
    esi: "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    grossEarnings: 0,
    totalDeductions: 0,
    netSalary: 0,
  });

  useEffect(() => {
    if (id) {
      fetchEmployeeData(id);
      if (isEditMode) {
        fetchSalaryData(id);
      }
    }
  }, [id, isEditMode]);

  useEffect(() => {
    calculateTotals();
  }, [formData]);

  const fetchEmployeeData = async (employeeId: string) => {
    try {
      setIsFetchingEmployee(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEmployee(response.data);
    } catch (error: any) {
      console.error("Error fetching employee:", error);
      setError(
        error.response?.data?.message || "Failed to fetch employee data"
      );
    } finally {
      setIsFetchingEmployee(false);
    }
  };

  const fetchSalaryData = async (employeeId: string) => {
    try {
      setIsFetchingSalary(true);

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/salary/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const salaryData = response.data;
      setFormData({
        basic: salaryData.basic?.toString() || "",
        hra: salaryData.hra?.toString() || "",
        otherAllowances: salaryData.otherAllowances?.toString() || "",
        pf: salaryData.deductions?.pf?.toString() || "",
        esi: salaryData.deductions?.esi?.toString() || "",
      });
    } catch (error: any) {
      console.error("Error fetching salary:", error);
      if (!error.response || error.response.status !== 404) {
        setError(
          error.response?.data?.message || "Failed to fetch salary data"
        );
      }
    } finally {
      setIsFetchingSalary(false);
    }
  };

  const calculateTotals = () => {
    const basic = parseFloat(formData.basic) || 0;
    const hra = parseFloat(formData.hra) || 0;
    const otherAllowances = parseFloat(formData.otherAllowances) || 0;
    const pf = parseFloat(formData.pf) || 0;
    const esi = parseFloat(formData.esi) || 0;

    const grossEarnings = basic + hra + otherAllowances;
    const totalDeductions = pf + esi;
    const netSalary = grossEarnings - totalDeductions;

    setCalculatedValues({
      grossEarnings,
      totalDeductions,
      netSalary,
    });
  };

  const handleInputChange = (field: keyof SalaryFormData, value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, "");
    setFormData((prev) => ({
      ...prev,
      [field]: numericValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      if (!formData.basic) {
        setError("Basic salary is required");
        return;
      }

      const basic = parseFloat(formData.basic);
      const hra = parseFloat(formData.hra) || 0;
      const otherAllowances = parseFloat(formData.otherAllowances) || 0;
      const pf = parseFloat(formData.pf) || 0;
      const esi = parseFloat(formData.esi) || 0;

      const salaryData = {
        basic,
        hra,
        otherAllowances,
        deductions: {
          pf,
          esi,
        },
        total: basic + hra + otherAllowances,
      };

      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/salary/${id}`,
        salaryData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const successMessage = isEditMode
          ? "Salary updated successfully!"
          : "Salary added successfully!";

        navigate("/salary", {
          state: { message: successMessage },
        });
      }
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} salary:`,
        error
      );
      setError(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "add"} salary`
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetchingEmployee || isFetchingSalary) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">
                {isFetchingEmployee
                  ? "Loading employee data..."
                  : "Loading salary data..."}
              </h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the information
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="flex items-left gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/salary")}
          className="h-9 w-9 p-0"
          disabled={isLoading}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditMode ? "Edit Salary" : "Add Salary"}
          </h1>
          <p className="text-muted-foreground">
            {employee
              ? `${isEditMode ? "Update" : "Add"} salary details for ${
                  employee.fullName
                }`
              : `${isEditMode ? "Update" : "Add"} salary details`}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Inactive Employee Warning */}
      {employee && !employee.isActive && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <p className="text-sm font-medium text-amber-800">
              Inactive Employee - Action Restricted
            </p>
          </div>
          <p className="mt-1 text-sm text-amber-700">
            This employee is marked as inactive
            {employee.resignationDate &&
              ` (resigned on ${new Date(
                employee.resignationDate
              ).toLocaleDateString()})`}
            . Salary cannot be added or modified for inactive employees. Please
            reactivate the employee first if you need to make changes.
          </p>
        </div>
      )}

      {/* Employee Info Card */}
      {employee && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Employee Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">{employee.fullName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium">{employee.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Designation</p>
                <p className="font-medium">{employee.designation || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <div className="mt-1">
                  {employee.isActive ? (
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Salary Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Salary Components
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="basic">
                  Basic Salary <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="basic"
                  type="text"
                  placeholder="Enter basic salary"
                  value={formData.basic}
                  onChange={(e) => handleInputChange("basic", e.target.value)}
                  disabled={isLoading || !employee?.isActive}
                />
                <p className="text-xs text-muted-foreground">
                  Base salary amount
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hra">HRA (House Rent Allowance)</Label>
                <Input
                  id="hra"
                  type="text"
                  placeholder="Enter HRA"
                  value={formData.hra}
                  onChange={(e) => handleInputChange("hra", e.target.value)}
                  disabled={isLoading || !employee?.isActive}
                />
                <p className="text-xs text-muted-foreground">
                  Housing rent allowance
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otherAllowances">Other Allowances</Label>
                <Input
                  id="otherAllowances"
                  type="text"
                  placeholder="Enter other allowances"
                  value={formData.otherAllowances}
                  onChange={(e) =>
                    handleInputChange("otherAllowances", e.target.value)
                  }
                  disabled={isLoading || !employee?.isActive}
                />
                <p className="text-xs text-muted-foreground">
                  Transport, medical, etc.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deductions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pf">Provident Fund (PF)</Label>
                <Input
                  id="pf"
                  type="text"
                  placeholder="Enter PF deduction"
                  value={formData.pf}
                  onChange={(e) => handleInputChange("pf", e.target.value)}
                  disabled={isLoading || !employee?.isActive}
                />
                <p className="text-xs text-muted-foreground">
                  Provident fund contribution
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="esi">ESI (Employee State Insurance)</Label>
                <Input
                  id="esi"
                  type="text"
                  placeholder="Enter ESI deduction"
                  value={formData.esi}
                  onChange={(e) => handleInputChange("esi", e.target.value)}
                  disabled={isLoading || !employee?.isActive}
                />
                <p className="text-xs text-muted-foreground">
                  Health insurance contribution
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calculated Values */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Salary Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-600 font-medium">
                  Gross Earnings
                </p>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  ₹{calculatedValues.grossEarnings.toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600 font-medium">
                  Total Deductions
                </p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  ₹{calculatedValues.totalDeductions.toLocaleString()}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm text-green-600 font-medium">Net Salary</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  ₹{calculatedValues.netSalary.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/salary")}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !employee?.isActive || !formData.basic}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Spinner variant="ellipsis" size={16} className="mr-2" />
                {isEditMode ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>{isEditMode ? "Update Salary" : "Add Salary"}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
